import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Label } from '@/components/shadcn/label'
import type { InAppNotificationImageResponse } from '@/api/model/inAppNotificationImageResponse'

export interface PendingImage {
  file: File
  previewUrl: string
}

interface Props {
  existingImages: InAppNotificationImageResponse[]
  pendingImages: PendingImage[]
  onAddFile: (file: File) => void
  onRemoveExisting: (id: string) => void
  onRemovePending: (previewUrl: string) => void
}

export function NotificationAttachments({
  existingImages, pendingImages, onAddFile, onRemoveExisting, onRemovePending,
}: Props) {
  const { t } = useTranslation('notifications')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    onAddFile(selected)
    e.target.value = ''
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('dialog.attachments.label')}</Label>
      <input type="file" accept="image/*" ref={fileInputRef} hidden onChange={handleImageSelect} />
      <div className="flex flex-wrap gap-2">
        {existingImages.map((img) => (
          <div key={img.id} className="relative h-[90px] w-[120px]">
            <img
              src={img.downloadUrl ?? img.path ?? ''}
              alt={img.filename ?? ''}
              className="h-full w-full rounded-md object-cover"
            />
            <button
              type="button"
              onClick={() => img.id && onRemoveExisting(img.id)}
              className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5 text-muted-foreground hover:text-foreground shadow"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
        {pendingImages.map((img) => (
          <div key={img.previewUrl} className="relative h-[90px] w-[120px]">
            <img src={img.previewUrl} alt={img.file.name} className="h-full w-full rounded-md object-cover" />
            <button
              type="button"
              onClick={() => onRemovePending(img.previewUrl)}
              className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5 text-muted-foreground hover:text-foreground shadow"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-[90px] w-[120px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground hover:bg-muted transition-colors"
        >
          {t('dialog.attachments.add')}
        </button>
      </div>
    </div>
  )
}
